import React, { useEffect, useState } from "react";
import {
  Users,
  Building2,
  Boxes,
  Wallet,
  TrendingUp,
  AlertCircle,
  Palette,
  ClipboardList,
  CheckCircle2,
  LayoutDashboard,
} from "lucide-react";
import { fetchZumaleAdminDashboard } from "../../services/dashboard";
import PageHeader from "../../components/PageHeader";
import StatTile from "../../components/StatTile";

/* ---------- formatting helpers ---------- */
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");

// Read a value tolerant of camelCase (pyhumps) or snake_case responses.
const pick = (obj, camel, snake) => obj?.[camel] ?? obj?.[snake] ?? 0;

/* ---------- subcomponents ---------- */
const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    {(title || subtitle) && (
      <div className="px-5 pt-5">
        {title && <h3 className="text-base font-semibold text-slate-900">{title}</h3>}
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

/* ---------- main component ---------- */
const ZumaleAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchZumaleAdminDashboard()
      .then((res) => {
        if (mounted) setData(res);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.detail || err.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const activeDevelopers = pick(data, "totalActiveDevelopers", "total_active_developers");
  const activePhases = pick(data, "activeProjectPhases", "active_project_phases");
  const inventory = pick(data, "totalInventoryActivePhases", "total_inventory_active_phases");
  const unitsSold = pick(data, "unitsSoldActivePhases", "units_sold_active_phases");
  const unitsUnsold = pick(data, "unitsUnsoldActivePhases", "units_unsold_active_phases");
  const revenue = pick(data, "revenueLast6Months", "revenue_last_6_months");
  const subscriptionRevenue = pick(data, "subscriptionRevenueLast6Months", "subscription_revenue_last_6_months");
  const idRevenue = pick(data, "idRevenueLast6Months", "id_revenue_last_6_months");
  const interiorExecutionRevenue = pick(data, "interiorExecutionRevenueLast6Months", "interior_execution_revenue_last_6_months");
  const projectedRevenue = pick(data, "projectedRevenueNext6Months", "projected_revenue_next_6_months");
  const overdueAmount = pick(data, "overduePaymentsAmount", "overdue_payments_amount");
  const overdueCount = pick(data, "overduePaymentsCount", "overdue_payments_count");
  const activeDesigners = pick(data, "activeInteriorDesigners", "active_interior_designers");
  const activeIdProjects = pick(data, "activeInteriorDesignProjects", "active_interior_design_projects");

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader icon={LayoutDashboard} title="Zumalestate Admin Dashboard" subtitle={today} />

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <StatTile key={i} loading />
          ))}
        </div>
      ) : (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile icon={Users} label="Active Developers" value={fmtInt(activeDevelopers)} accent="emerald" />
            <StatTile icon={Building2} label="Active Project Phases" value={fmtInt(activePhases)} accent="indigo" />
            <StatTile
              icon={Boxes}
              label="Inventory (Active Phases)"
              value={fmtInt(inventory)}
              subtitle={`${fmtInt(unitsSold)} sold · ${fmtInt(unitsUnsold)} unsold`}
              accent="slate"
            />
            <StatTile
              icon={AlertCircle}
              label="Overdue Payments"
              value={fmtINR(overdueAmount)}
              subtitle={`${fmtInt(overdueCount)} installment(s)`}
              accent="rose"
            />
            <StatTile
              icon={Wallet}
              label="Revenue (Last 6 Months)"
              value={fmtINR(revenue)}
              subtitle={`Dev ${fmtINR(subscriptionRevenue)} · ID ${fmtINR(idRevenue)} · Exec ${fmtINR(interiorExecutionRevenue)}`}
              accent="flamingo"
            />
            <StatTile
              icon={TrendingUp}
              label="Projected Revenue (Next 6 Months)"
              value={fmtINR(projectedRevenue)}
              subtitle="Scheduled subscription installments"
              accent="amber"
            />
            <StatTile
              icon={Palette}
              label="Active Interior Designers"
              value={fmtInt(activeDesigners)}
              accent="indigo"
            />
            <StatTile
              icon={ClipboardList}
              label="Active Interior Design Projects"
              value={fmtInt(activeIdProjects)}
              accent="emerald"
            />
          </div>

            {/* Inventory + Revenue breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionCard title="Inventory in Active Phases" subtitle="Units across all active project phases">
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600 flex items-center gap-1.5">
                        <CheckCircle2 size={14} className="text-emerald-600" /> Sold
                      </span>
                      <span className="font-semibold tabular-nums">{fmtInt(unitsSold)}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: `${inventory ? (unitsSold / inventory) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm mb-1 mt-4">
                      <span className="text-slate-600">Unsold</span>
                      <span className="font-semibold tabular-nums">{fmtInt(unitsUnsold)}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-slate-400"
                        style={{ width: `${inventory ? (unitsUnsold / inventory) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-center px-4">
                    <p className="text-3xl font-bold text-slate-900 tabular-nums">{fmtInt(inventory)}</p>
                    <p className="text-xs text-slate-500 mt-1">Total units</p>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Revenue" subtitle="Zumalestate income from developers, interior designers & interior execution (last 6 months)">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Received (6 mo)</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">{fmtINR(revenue)}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Projected (6 mo)</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">{fmtINR(projectedRevenue)}</p>
                  </div>
                </div>
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mt-4 mb-2">By stream</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Developer</p>
                    <p className="text-base font-semibold text-slate-800 tabular-nums mt-1">{fmtINR(subscriptionRevenue)}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Interior Designers</p>
                    <p className="text-base font-semibold text-slate-800 tabular-nums mt-1">{fmtINR(idRevenue)}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Interior Execution</p>
                    <p className="text-base font-semibold text-slate-800 tabular-nums mt-1">{fmtINR(interiorExecutionRevenue)}</p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-3">Referral commission & move-in services — coming soon.</p>
              </SectionCard>
            </div>
          </>
        )}
    </div>
  );
};

export default ZumaleAdminDashboard;
